import { BLOCK_TYPES } from "./blocks";

export const SLOT_ATTRIBUTE_TYPES = {
  RICH_TEXT: "rich_text",
  STRING: "string",
  IMAGE: "image",
  VIDEO: "video",
  AUDIO: "audio",
  COLLECTION: "collection",
  WORKOUT: "workout",
  ACTION: "action",
  FORM: "form",
  ATTACHMENT: "attachment",
  ICON_LIST: "icon_list",
  ADDRESS: "address",
} as const;

export type SlotAttributeType =
  typeof SLOT_ATTRIBUTE_TYPES[keyof typeof SLOT_ATTRIBUTE_TYPES];

export type SlotAttributeDefinition = {
  key: string;
  label: string;
  type: SlotAttributeType;
};

export const BLOCK_SLOT_ATTRIBUTES: Record<string, SlotAttributeDefinition[]> = {
  [BLOCK_TYPES.TEXT_BLOCK]: [
    { key: "content", label: "Text", type: SLOT_ATTRIBUTE_TYPES.RICH_TEXT },
  ],
  [BLOCK_TYPES.HERO_BLOCK]: [
    { key: "image", label: "Image", type: SLOT_ATTRIBUTE_TYPES.IMAGE },
    { key: "video", label: "Video", type: SLOT_ATTRIBUTE_TYPES.VIDEO },
    { key: "audio", label: "Audio", type: SLOT_ATTRIBUTE_TYPES.AUDIO },
  ],
  [BLOCK_TYPES.BUTTON_BLOCK]: [
    { key: "text", label: "Text", type: SLOT_ATTRIBUTE_TYPES.STRING },
    {
      key: "post_purchase_text",
      label: "Post-Purchase Text",
      type: SLOT_ATTRIBUTE_TYPES.STRING,
    },
    { key: "action", label: "Action", type: SLOT_ATTRIBUTE_TYPES.ACTION },
  ],
  [BLOCK_TYPES.WORKOUT_BLOCK]: [
    { key: "workout", label: "Workout", type: SLOT_ATTRIBUTE_TYPES.WORKOUT },
    { key: "action", label: "Action", type: SLOT_ATTRIBUTE_TYPES.ACTION },
  ],
  [BLOCK_TYPES.COLLECTION_BLOCK]: [
    {
      key: "collection",
      label: "Collection",
      type: SLOT_ATTRIBUTE_TYPES.COLLECTION,
    },
  ],
  [BLOCK_TYPES.ATTACHMENT_BLOCK]: [
    {
      key: "attachment",
      label: "Attachment",
      type: SLOT_ATTRIBUTE_TYPES.ATTACHMENT,
    },
    { key: "image", label: "Image", type: SLOT_ATTRIBUTE_TYPES.IMAGE },
  ],
  [BLOCK_TYPES.ICON_LIST_BLOCK]: [
    { key: "icon_list", label: "Items", type: SLOT_ATTRIBUTE_TYPES.ICON_LIST },
  ],
  [BLOCK_TYPES.IFRAME_BLOCK]: [
    { key: "url", label: "URL", type: SLOT_ATTRIBUTE_TYPES.STRING },
  ],
  [BLOCK_TYPES.IMAGE_BLOCK]: [
    { key: "image", label: "Image", type: SLOT_ATTRIBUTE_TYPES.IMAGE },
  ],
  [BLOCK_TYPES.MAP_BLOCK]: [
    { key: "address", label: "Address", type: SLOT_ATTRIBUTE_TYPES.ADDRESS },
  ],
  [BLOCK_TYPES.FORM_BLOCK]: [
    { key: "form", label: "Form", type: SLOT_ATTRIBUTE_TYPES.FORM },
    { key: "action", label: "Action", type: SLOT_ATTRIBUTE_TYPES.ACTION },
  ],
};

export const getSlotAttributes = (
  blockType: string
): SlotAttributeDefinition[] => {
  return BLOCK_SLOT_ATTRIBUTES[blockType] || [];
};

export const isSlotAttribute = (
  blockType: string,
  fieldKey: string
): boolean => {
  const slots = BLOCK_SLOT_ATTRIBUTES[blockType];
  if (!slots) return false;

  return slots.some((slot) => slot.key === fieldKey);
};

export const getSlotAttributeType = (
  blockType: string,
  fieldKey: string
): SlotAttributeType | null => {
  const slots = BLOCK_SLOT_ATTRIBUTES[blockType];
  if (!slots) return null;

  return slots.find((slot) => slot.key === fieldKey)?.type || null;
};

export const SLOT_ATTRIBUTE_SLUG_PREFIX = "blocks.";

export const slotAttributeSlug = (
  blockUuid: string,
  fieldKey: string
): string => `${SLOT_ATTRIBUTE_SLUG_PREFIX}${blockUuid}.${fieldKey}`;

export const parseSlotAttributeSlug = (
  slotSlug: string | null | undefined
): { blockUuid: string; fieldKey: string } | null => {
  if (!slotSlug?.startsWith(SLOT_ATTRIBUTE_SLUG_PREFIX)) return null;

  const parts = slotSlug.split(".");
  if (parts.length < 3) return null;

  return {
    blockUuid: parts[1],
    fieldKey: parts.slice(2).join("."),
  };
};
